import {
  useEffect,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { WORKSPACE_FILES_SEARCH_PARAM } from "@/lib/navigation/appNavigation";
import type { WorkspaceSearch } from "@/pages/workspace/providers/WorkspaceProvider";

/**
 * Picks up a search handed over in the address: the Workspaces page's "Åbn workspace"
 * carries the reader's search into the workspace (LAR-235), so it is not typed twice.
 * The term is applied to the whole workspace and then dropped from the address, so a
 * reload or a later visit does not bring it back. The field is focused with the term
 * selected, which shows the search is active and lets typing replace it.
 */
export const useSearchFromNavigation = (
  searchString: string,
  setSearch: Dispatch<SetStateAction<WorkspaceSearch>>,
  inputRef: RefObject<HTMLInputElement | null>
): void => {
  const location = useLocation();
  const navigate = useNavigate();
  const [termToSelect, setTermToSelect] = useState<string | null>(null);
  const handedOver =
    new URLSearchParams(location.search)
      .get(WORKSPACE_FILES_SEARCH_PARAM)
      ?.trim() ?? "";

  useEffect(() => {
    if (!handedOver) return;
    setSearch((prev) => ({ ...prev, searchString: handedOver, folders: [] }));
    setTermToSelect(handedOver);

    const params = new URLSearchParams(location.search);
    params.delete(WORKSPACE_FILES_SEARCH_PARAM);
    const rest = params.toString();
    navigate(
      { pathname: location.pathname, search: rest ? `?${rest}` : "" },
      { replace: true, state: location.state }
    );
  }, [
    handedOver,
    location.pathname,
    location.search,
    location.state,
    navigate,
    setSearch,
  ]);

  // The field shows the term only after the render that follows the state update.
  useEffect(() => {
    if (termToSelect === null || searchString !== termToSelect) return;
    inputRef.current?.focus();
    inputRef.current?.select();
    setTermToSelect(null);
  }, [termToSelect, searchString, inputRef]);
};
